/**
 * Dimension authoring helpers.
 *
 * `defineDimension<Config, State, Options, Diagnostics>()(config)` is
 * the entry point for declaring a dimension. It is curried so that the
 * four explicitly-supplied generics (`Config` / `State` / `Options` /
 * `Diagnostics`) can be specified at the outer call while `Key` and
 * `Fragments` are inferred from the config object at the inner call.
 *
 * The returned value is the full `Dimension` object — the same shape
 * the author wrote — except that `.fragments` is replaced by wrapped
 * versions whose returned config values carry a non-enumerable
 * `__dimension` back-reference. Exposing the full dimension lets
 * callers reach `dimension.isValid(...)` / `dimension.diagnose?.(...)`
 * directly when they need low-level migration or testing hooks, while
 * ability authors destructure `dimension.fragments` for the builder API.
 *
 * The dispatcher in `ability.ts` and helper utilities follow the
 * per-fragment `__dimension` back-reference to find the dimension
 * responsible for evaluating each fragment. That's what makes the
 * system fully decoupled without a central registry.
 */

import type {
  Dimension,
  DimensionArgs,
  ConfigShape,
  FragmentMap,
  StateShape,
} from './types';

/**
 * Attaches a non-enumerable `__dimension` property to `value` and
 * returns it. The descriptor is locked down (`writable: false`,
 * `configurable: false`) so the back-reference cannot be tampered with
 * after the fragment is produced.
 */
function attachDimension<Value extends object>(
  value: Value,
  dimension: unknown
): Value {
  Object.defineProperty(value, '__dimension', {
    value: dimension,
    enumerable: false,
    writable: false,
    configurable: false,
  });
  return value;
}

/**
 * Wraps each entry in `fragments` with a closure that pipes the
 * original return value through `attachDimension`, producing the
 * fragment-with-dimension shape that the rest of the system consumes.
 *
 * The single `as never` at the return statement is the boundary cast
 * that bridges the loose internal `Record<string, ...>` builder back to
 * the precise mapped-type signature. Per-call there is one closure
 * allocated at definition time; calling the wrapped fragment is then a
 * direct call into that closure with no further allocation overhead.
 */
function wrapFragments<Frags extends FragmentMap<object>, Dim>(
  fragments: Frags,
  dimension: Dim
): {
  [Key in keyof Frags]: (
    ...args: Parameters<Frags[Key]>
  ) => ReturnType<Frags[Key]> & {__dimension: Dim};
} {
  const result: FragmentMap<object> = {};
  for (const [name, fragmentFn] of Object.entries(fragments)) {
    result[name] = (...args) => attachDimension(fragmentFn(...args), dimension);
  }
  return result as never;
}

/**
 * The shape of a fragments record after being passed through
 * `wrapFragments`. Each fragment's return type carries the
 * `__dimension` back-reference; everything else (args, base return
 * shape) is preserved verbatim.
 */
type WrappedFragments<Frags extends FragmentMap<object>, Dim> = {
  [Key in keyof Frags]: (
    ...args: Parameters<Frags[Key]>
  ) => ReturnType<Frags[Key]> & {__dimension: Dim};
};

/**
 * Defines a dimension and returns it with wrapped fragments.
 *
 * Curried because TypeScript does not support partial type-argument
 * inference. The outer call captures the authored generics; the inner
 * call infers `Key` (as a literal via `const Key`) and `Fragments` from
 * the config object.
 *
 * The returned value is structurally a `Dimension` whose `.fragments`
 * are the wrapped versions, so author conventions look like:
 *
 *     export const appInfo = defineDimension<...>()({...});
 *     export const {inApps, notInApps} = appInfo.fragments;
 *
 * `appInfo` itself is callable for parity checks
 * (`appInfo.isValid(state, config, options)`), and the destructured
 * fragments are what ability authors compose with.
 *
 * @typeParam Config      Config value type produced by this dimension's fragments.
 * @typeParam State       Slice of state this dimension reads.
 * @typeParam Options     Caller-supplied context this dimension reads. Defaults to
 *                        `never` so dimensions opt in to options.
 * @typeParam Diagnostics Diagnostic shape returned by `diagnose()`.
 */
export function defineDimension<
  Config extends ConfigShape,
  State extends StateShape = StateShape,
  Options = never,
  Diagnostics = string,
>() {
  return <const Key extends string, Fragments extends FragmentMap<Config>>(
    config: Dimension<Key, Config, State, Fragments, Options, Diagnostics>
  ): Omit<
    Dimension<Key, Config, State, Fragments, Options, Diagnostics>,
    'fragments' | 'isValid' | 'diagnose'
  > & {
    // Consumer-facing arity: dimensions that opt out of options can be
    // evaluated as `dimension.isValid(state, config)` — the third slot
    // only appears when the dimension declares `Options`. The authored
    // `config.isValid` keeps the fixed `(state, config, options)` shape.
    isValid(...args: DimensionArgs<State, Config, Options>): boolean;
    diagnose?(
      ...args: DimensionArgs<State, Config, Options>
    ): [boolean, Diagnostics, Record<string, unknown>?];
    readonly fragments: WrappedFragments<
      Fragments,
      Dimension<Key, Config, State, Fragments, Options, Diagnostics>
    >;
  } => {
    const dimension = {} as Record<string, unknown>;
    const wrappedFragments = wrapFragments(config.fragments, dimension);
    Object.assign(dimension, config, {
      fragments: wrappedFragments,
    });
    return dimension as never;
  };
}

/**
 * Builds a `Record<Key, boolean>` from an array of keys.
 *
 * Typed wrapper around `Object.fromEntries` — TypeScript cannot narrow
 * `Object.fromEntries`'s return from `Record<string, V>` to
 * `Record<Key, V>`, so this helper internalises the cast. Common in
 * dimension `isValid` implementations that check membership against a
 * set of allowed values.
 */
export function createBooleanMap<Key extends PropertyKey>(
  keys: readonly Key[],
  value: boolean
): Record<Key, boolean> {
  return Object.fromEntries(keys.map((key) => [key, value])) as Record<
    Key,
    boolean
  >;
}
